import * as React from "react"
import { useState, Suspense } from "react"
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import { useNavigate } from "react-router-dom"

import { h3, h4, body, bodyLarge } from "../commonStyles/Fonts";

import BackgroundImage from "../assets/images/login-form-background.png";
import CheckIcon from "../assets/icons/checkbox-white.svg";
import { ReactComponent as GoogleIcon } from "../assets/icons/google.svg?react";
import { ReactComponent as FacebookIcon } from "../assets/icons/facebook.svg?react";
import { ReactComponent as AppleIcon } from "../assets/icons/apple.svg?react";
import { ReactComponent as PasswordVisibleIcon } from "../assets/icons/password-eye-visible.svg?react";
import { ReactComponent as PasswordHiddenIcon } from "../assets/icons/password-eye-hidden.svg?react";

import { styled } from "../../stitches.config"
import Link from "../../components/Link";
import { useStores } from "../../helpers";

import { useAuth } from "../../context/AuthContext"
import { signupUser } from "../../api/account"
import { useQuery } from "../pages/SearchPage";

type RegisterFormValues = {
    email: string;
    password: string;
    privacyPolicy: boolean;
}

// TODO animacja submitowania/ostylować błędy logowania
const RegistrationForm: React.FC = () => {
    const [, dispatch] = useAuth();
    const navigate = useNavigate();
    const { UIStore } = useStores();
    const [isPasswordVisible, setPasswordVisible] = useState(false);
    const query = useQuery();
    const [submitting, setSubmitting] = useState<boolean>(false)
    const initialValues: RegisterFormValues = {
        email: query.get("email") || "",
        password: "",
        privacyPolicy: false
    }
    const validationSchema = Yup.object().shape({
        email: Yup.string()
            .required("Pole wymagane")
            .email("Wprowadź poprawny adres e-mail"),
        password: Yup.string()
            .required('Pole wymagane')
            .min(8, 'Hasło musi zawierać minimum 8 znaków'),
        //.matches(/[a-zA-Z]/, 'Hasło musi '), //tutaj pewnie jakieś wymagania do ustalenia,
        privacyPolicy: Yup.boolean()
            .required("Musisz zaakceptować warunki")
            .oneOf([true], "Musisz zaakceptować warunki.")
    })

    const handleSubmit = ({ email, password }: RegisterFormValues, { setErrors }: { setErrors: any }) => {
        signupUser({ email, password })
            .then(_res => {
                dispatch({ type: 'register' })
                UIStore.setShowLoginModal(true);
                navigate("/dashboard", { replace: true })
            })
            .catch(err => {
                console.log(err)
                setErrors(err.errors)
                // dispatch({ type: 'set_error', payload: err })
            })
    }

    const togglePasswordVisible = () => setPasswordVisible((prev) => !prev);

    return (
        <FormWrapper>
            <FormWrapperInputs>
                <FormTitle>
                    <p className={h3()}>Załóż konto</p>
                </FormTitle>
                <FormWrapperInputsButtons>
                    <a href="/security/auth?authclient=facebook"><FacebookIcon /></a>
                    <a href="/security/auth?authclient=google"><GoogleIcon /></a>
                    {/* <button><AppleIcon /></button> */}
                </FormWrapperInputsButtons>

                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}>
                    {({ errors, touched }) => (
                        <Form className={body()}>
                            lub
                            <label className={`${errors.email && touched.email && "show-error-input-text"}`} htmlFor="email">Email</label>
                            <Field
                                type="text"
                                name="email"
                                className={`${bodyLarge()} ${errors.email && touched.email && "show-error-input-border"}`}
                                placeholder="Wpisz swój email" />
                            {errors.email && touched.email && <span className="validationError">{errors.email}</span>}
                            <label className={`${errors.password && touched.password && "show-error-input-text"}`} htmlFor="password">Hasło</label>
                            <PasswordWrapper>
                                <Field
                                    type={isPasswordVisible ? "text" : "password"}
                                    name="password"
                                    className={`${bodyLarge()} ${errors.password && touched.password && "show-error-input-border"}`}
                                    placeholder="Wpisz swoje hasło"
                                />
                                <Suspense fallback={null}>
                                    <PasswordIcon onClick={togglePasswordVisible}>
                                        {isPasswordVisible ? (
                                            <PasswordVisibleIcon />
                                        ) : (
                                            <PasswordHiddenIcon />
                                        )}
                                    </PasswordIcon>
                                </Suspense>
                            </PasswordWrapper>
                            {errors.password && touched.password && <span className="validationError">{errors.password}</span>}
                            <div className="checkbox"><Field className={`${bodyLarge()} ${errors.privacyPolicy && touched.privacyPolicy && "show-error-checkbox-border"}`} type="checkbox" name="privacyPolicy" />
                                Akceptuje
                                <Link to="/info/regulamin">regulamin</Link> i <Link to="/info/polityka-prywatnosci">politykę prywatności</Link>
                            </div>
                            {errors.privacyPolicy && touched.privacyPolicy && <span className="validationError">{errors.privacyPolicy}</span>}
                            <button type="submit" className={bodyLarge()} >Załóż konto</button>
                        </Form>
                    )}
                </Formik>
                <FormWrapperInputsLink className={bodyLarge()}>
                    <Link to="/login">
                        Masz konto? <span>Zaloguj się</span>
                    </Link>
                </FormWrapperInputsLink>
            </FormWrapperInputs>
            <FormWrapperInfo>
                <p className={h3()}>Korzystaj w pełni z na<span>:</span>Temat</p>
                <FormWrapperInfoList className={h4()}>
                    <li>Możliwość wyłączenia reklam display i korzystanie z pełni funkcjonalności serwisu</li>
                    <li>Twórz swój feed</li>
                    <li>Wybieraj co widzisz i w jakiej kolejności</li>
                    <li>Otrzymuj dedykowany newsletter od redaktora naczelnego</li>
                    <li>Zapisuj tagi i śledź wydarzenia, które Cię interesują</li>
                    <li>Tylko 4,99 zł/miesięcznie</li>
                </FormWrapperInfoList>
            </FormWrapperInfo>
        </FormWrapper>
    );
}

const FormWrapper = styled("div", {
    display: "flex",
    width: "100%",
    justifyContent: "center",
    background: "$grayscale0",
    "@bp2": {
        width: "auto",
    },
    "& .validationError": {
        color: "$aszdziennikPrimary",
        fontSize: "12px",
        lineHeight: "16px",
        marginTop: "8px",
    }
})

// formularz


const FormTitle = styled("div", {
    display: "flex",
    alignItems: "flex-end",
    "& a": {
        paddingTop: "0px",
        "@bp4": {
            display: "none",
            paddingTop: "24px",
        }
    },
    "& p": {
        width: "332px",
        margin: 0,
    },
})

const FormWrapperInputs = styled("div", {
    padding: "32px 16px 32px 16px",
    color: "$grayscale100",
    "@bp4": {
        padding: "36px 40px 32px 40px",
    },
    "& .logo": {
        marginBottom: "28px",
        height: "16px",
        display: "none",
        "@bp4": {
            display: "block",
        },
        "& svg": {
            width: "98px",
            height: "16px",
            "& .dots": {
                fill: "$primary",
            }
        },
    },

    "& form": {
        display: "flex",
        flexDirection: "column",
        "& label": {
            margin: "24px 0 4px 0"
        },
        "& input": {
            border: "1px solid $grayscale25",
            fontWeight: "500",
            padding: "12px 14px",
            transition: "0.2s",
            caretColor: "$primary",
            color: "$grayscale100Fixed",
            "&::placeholder": {
                color: "$grayscale50",
            },
            "&:hover": {
                border: "1px solid $grayscale50",
            },
            "&:focus": {
                border: "1px solid $grayscale50",
            },
        },
        "& button": {
            background: "$primary",
            borderWidth: "0px",
            color: "$grayscale0",
            margin: "24px 0",
            fontWeight: "500",
            padding: "12px 14px"
        },
        "& input[type = checkbox]": {
            marginRight: "8px",
            borderRadius: "0px",
            width: "16px",
            height: "16px",
            position: "relative",
            "&::before": {
                borderRadius: "0px",
                width: "16px",
                height: "16px",
                position: "absolute",
                border: "1px solid $grayscale50",
                content: "",
                right: "0px",
                top: "0px",
                display: "flex",
                color: "green",
                background: "$grayscale0",
                // transition: "0.2s",
            },
            "&:checked": {
                "&::before": {
                    backgroundColor: "$primary",
                    backgroundImage: `url("${CheckIcon}")`,
                    backgroundPosition: "center",
                    backgroundSize: "12px",
                    backgroundRepeat: "no-repeat",
                    border: "1px solid $primary",
                }
            }
        }
    },

    "& .checkbox": {
        margin: "24px 0 0 0",
        display: "flex",
        alignItems: "center",
        "& a": {
            color: "$grayscale100",
            margin: "0 3px",
        }
    },

    ".show-error-input-border": {
        border: "1px solid $aszdziennikPrimary",
        "&:hover": {
            border: "1px solid $aszdziennikPrimary",
        },
        "&:focus": {
            border: "1px solid $aszdziennikPrimary",
        },
    },
    ".show-error-input-text": {
        color: "$aszdziennikPrimary",
    },
    ".show-error-checkbox-border": {
        "&::before": {
            border: "1px solid $aszdziennikPrimary !important",
        }
    }
})

const FormWrapperInputsLink = styled("div", {
    width: "100%",
    display: "flex",
    "& a": {
        margin: "0 auto",
        color: "$grayscale100",
        "& span": {
            color: "$primary",
        },
        "&:hover": {
            textDecoration: "none",
        }
    }
})


const FormWrapperInputsButtons = styled("div", {
    display: "flex",
    justifyContent: "space-between",
    margin: "24px 0",
    "& a": {
        boxSizing: "border-box",
        width: "48%",
        height: "40px",
        background: "none",
        border: "1.5px solid $grayscale25",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        "@bp4": {
            height: "48px",
        },
        "& svg": {
            width: "18px",
            height: "18px",
            "@bp4": {
                width: "20px",
                height: "20px",
            },
        },
    },
});

// informacyjna część formularza

const FormWrapperInfo = styled("div", {
    padding: "36px 46px 0 40px",
    backgroundColor: "rgba(19, 18, 18, 0.8)",
    color: "$grayscale0Fixed",
    backgroundImage: `url("${BackgroundImage}")`,
    backgroundSize: "cover",
    display: "none",
    flexDirection: "column",
    "@bp4": {
        display: "flex",
    },

    "& p": {
        width: "332px",
        margin: 0,
        color: "#fff",

        "& span": {
            color: "$primary",
        }
    },

    "& a": {
        width: "20px",
        height: "20px",
        margin: "0 0 25px auto",
    }
})


const FormWrapperInfoList = styled("ul", {
    width: "332px",
    paddingLeft: "0px",
    listStyle: "none",
    marginTop: "24px",
    "& li": {
        position: "relative",
        paddingLeft: "16px",
        marginBottom: "24px",
        '&:before': {
            position: "absolute",
            left: 0,
            top: "6px",
            content: " ",
            width: "8px",
            height: "8px",
            background: "$primary",
            borderRadius: "50%",
        },
    }
})

// animacje 

const AnimationWrapper = styled("div", {

    "&.swipe-enter": {
        position: "absolute",
        top: 0,
        opacity: 0,
        transform: "translateX(100%)",

        // opacity: 0,
        // transform: "translate(0, 25px)",
        zIndex: 10
    },

    "&.swipe-enter.swipe-enter-active": {
        position: "initial",
        opacity: 1,
        transform: "translateX(0)",
        transition: "opacity 250ms ease-out, transform 300ms ease"

        // opacity: 1,
        // transform: "translate(0, 0)",
        // transition: "opacity 250ms ease-out, transform 300ms ease"
    },

    "&.swipe-exit": {
        position: "initial",
        top: 0,
        bottom: 0,
        opacity: 1,
        transform: "translateX(0)",

        // opacity: 1,
        // transform: "translate(0, 0)"
    },

    "&.swipe-exit-active": {
        position: "absolute",
        top: 0,
        opacity: 0,
        transform: "translateX(-100%)",
        transition: "opacity 250ms ease-out, transform 300ms ease"

        // opacity: 0,
        // transform: "translate(0, 30px)",
        // transition: "opacity 250ms ease-out, transform 300ms ease"
    }
})

const PasswordWrapper = styled("div", {
    width: "100%",
    position: "relative",
    input: {
        width: "100%",
    },
});

const PasswordIcon = styled("div", {
    position: "absolute",
    top: "16px",
    right: "16px",
    cursor: "pointer",
    userSelect: 'none'
});


export default RegistrationForm